import { z } from 'zod'

const decimalStr = (msg = 'Geçerli bir tutar giriniz.') =>
  z.coerce.string().refine(
    (v) => /^\d+(\.\d{1,4})?$/.test(v),
    { message: msg },
  )

/**
 * Çalışan Maliyet Kartı — Muhasebe modülü.
 * Aylık brüt maaş, işveren SGK payı ve diğer giderleri kayıt altına alır.
 * Saatlik efektif maliyet = (brütMaaş + SGK + diğerGiderler) / aylıkÇalışmaSaati
 */
export const EmployeeCostSchema = z.object({
  userId: z.string({ required_error: 'Çalışan seçimi zorunludur.' }).uuid(),
  /** YYYY-MM formatında dönem */
  period: z
    .string({ required_error: 'Dönem zorunludur.' })
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-MM formatında olmalıdır.'),
  /** Brüt maaş */
  grossSalary: decimalStr('Geçerli bir brüt maaş giriniz.'),
  /** İşveren SGK payı (~%22.5 brüt üzerinden — kullanıcı girer) */
  employerSgkContribution: decimalStr().optional().default('0'),
  /** Yemek, ulaşım, telefon vb. */
  otherCosts: decimalStr().optional().default('0'),
  notes: z.string().max(500).optional(),
})

/**
 * Genel Gider — kira, elektrik, yazılım lisansı vb.
 * Projelere saat oranında dağıtılır.
 */
export const OverheadExpenseSchema = z.object({
  /** YYYY-MM formatında dönem */
  period: z
    .string({ required_error: 'Dönem zorunludur.' })
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-MM formatında olmalıdır.'),
  category: z
    .string({ required_error: 'Kategori zorunludur.' })
    .min(1)
    .max(30),
  description: z
    .string({ required_error: 'Açıklama zorunludur.' })
    .min(3)
    .max(300),
  /** Girilen tutar. Kayıtta `amount` her zaman KDV HARİÇ matrahtır (bkz. amountIncludesKdv). */
  amount: decimalStr('Geçerli bir tutar giriniz.'),
  currency: z.string().length(3).default('TRY'),
  /** Döviz için 1 birimin TL kuru; boşsa sunucu TCMB kurunu uygular. Kayıtta `amount` TL'ye çevrilir. */
  exchangeRate: z.coerce.string().refine((v) => /^\d+(\.\d{1,6})?$/.test(v), { message: 'Geçerli bir kur giriniz.' }).nullable().optional(),
  /** KDV oranı (%): 0, 1, 10, 20 veya serbest — boşsa KDV'siz */
  kdvRate: decimalStr().nullable().optional(),
  /** KDV tutarı: sunucu hesaplar (oran varsa); yalnızca eski istemciler gönderir */
  kdvAmount: decimalStr().nullable().optional(),
  /** true ise `amount` KDV dahil girilmiştir; sunucu matrah (KDV hariç) ve KDV'yi ayırıp saklar. Kaydedilmez. */
  amountIncludesKdv: z.boolean().optional(),
  notes: z.string().max(500).optional(),
})

/**
 * Hizmet alımı (danışmanlık, yazılım, ulaşım…): tutar METİN (Decimal). Daha önce uçta doğrulama yoktu (`String(body.x)`),
 * geçersiz tutar sunucu hatasına (500) düşüyordu.
 */
export const ServicePurchaseSchema = z.object({
  title: z.string({ required_error: 'Başlık zorunludur.' }).trim().min(1, 'Başlık zorunludur.').max(200),
  description: z.string().max(1000).nullable().optional(),
  amount: decimalStr('Geçerli bir tutar giriniz.'),
  currency: z.string().length(3).optional(),
  exchangeRate: z.coerce.string().refine((v) => /^\d+(\.\d{1,6})?$/.test(v), { message: 'Geçerli bir kur giriniz.' }).nullable().optional(),
  date: z.string({ required_error: 'Tarih zorunludur.' }).refine((v) => !Number.isNaN(Date.parse(v)), 'Geçerli bir tarih giriniz.'),
  category: z.string().min(1).max(30).optional(),
  invoiceNo: z.string().max(100).nullable().optional(),
  kdvRate: decimalStr().nullable().optional(),
  kdvAmount: decimalStr().nullable().optional(),
  /** Genel gider şemasıyla aynı: true ise `amount` KDV dahil girilmiştir; kayıtta amount KDV hariç matrahtır */
  amountIncludesKdv: z.boolean().optional(),
  tevkifatRate: decimalStr().nullable().optional(),
  tevkifatAmount: decimalStr().nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
  vendorId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
})
export const UpdateServicePurchaseSchema = ServicePurchaseSchema.partial()
export type ServicePurchaseInput = z.infer<typeof ServicePurchaseSchema>
export type UpdateServicePurchaseInput = z.infer<typeof UpdateServicePurchaseSchema>

/**
 * Muhasebe görünürlük ayarları — sadece TENANT_ADMIN değiştirebilir.
 */
export const TenantFinanceSettingsSchema = z.object({
  /** Muhasebe rolü bütçe/proje maliyet raporlarını görebilir mi? */
  accountantCanViewReports: z.boolean().optional(),
  /** Muhasebe rolü diğer çalışanların maaş detaylarını görebilir mi? */
  accountantCanViewSalaries: z.boolean().optional(),
  /** SGK işveren payı varsayılan oranı (%) — yeni profil önerisi için */
  sgkEmployerRateDefault: z.coerce.number().min(0).max(100).optional(),
  /** SGK çalışan payı oranı (%) */
  sgkEmployeeRate: z.coerce.number().min(0).max(100).optional(),
  /** Damga vergisi oranı (%) */
  stampTaxRate: z.coerce.number().min(0).max(10).optional(),
  /** İşsizlik sigortası işveren payı oranı (%) */
  unemploymentInsuranceEmployerRate: z.coerce.number().min(0).max(10).optional(),
  /** İşsizlik sigortası çalışan payı oranı (%) */
  unemploymentInsuranceEmployeeRate: z.coerce.number().min(0).max(10).optional(),
  /** Varsayılan KDV oranı (%) — yeni fatura/hakediş formlarında önerilen değer */
  kdvRateDefault: z.coerce.number().min(0).max(100).optional(),
})

// ─── Çek / Senet Enstrümanları ──────────────────────────────────────────────

export const InstrumentType = z.enum(['CHECK', 'PROMISSORY_NOTE'])
export const InstrumentDirection = z.enum(['INCOMING', 'OUTGOING'])
export const PaymentMethod = z.enum(['CASH', 'BANK_TRANSFER', 'CHECK', 'PROMISSORY_NOTE', 'BARTER'])

export const IncomingInstrumentStatus = z.enum([
  'PORTFOLIO',
  'AT_BANK_COLLECTION',
  'AT_BANK_GUARANTEE',
  'COLLECTED',
  'ENDORSED',
  'DISHONORED',
  'RETURNED',
  'IN_LEGAL',
])

export const OutgoingInstrumentStatus = z.enum([
  'ISSUED',
  'CLEARED',
  'CANCELLED',
])

export const CreatePaymentInstrumentSchema = z.object({
  type: InstrumentType,
  direction: InstrumentDirection,
  serialNumber: z.string({ required_error: 'Seri numarası zorunludur.' }).min(1).max(50),
  drawerName: z.string({ required_error: 'Keşideci adı zorunludur.' }).min(1).max(200),
  bankName: z.string().max(100).optional(),
  branchName: z.string().max(100).optional(),
  branchCode: z.string().max(20).optional(),
  accountNumber: z.string().max(50).optional(),
  amount: decimalStr('Geçerli bir tutar giriniz.'),
  currency: z.string().length(3).default('TRY'),
  issueDate: z.string({ required_error: 'Düzenlenme tarihi zorunludur.' }),
  maturityDate: z.string({ required_error: 'Vade tarihi zorunludur.' }),
  customerId: z.string().uuid().optional(),
  vendorId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  description: z.string().max(500).optional(),
})

export const UpdatePaymentInstrumentSchema = CreatePaymentInstrumentSchema.partial().omit({
  type: true,
  direction: true,
})

export const InstrumentStatusTransitionSchema = z.object({
  toStatus: z.string({ required_error: 'Hedef durum zorunludur.' }),
  transactionDate: z.string({ required_error: 'İşlem tarihi zorunludur.' }),
  endorsedToVendorId: z.string().uuid().optional(),
  bankAccountInfo: z.string().max(200).optional(),
  notes: z.string().optional(),
})

export const CreatePaymentAllocationSchema = z.object({
  customerPaymentId: z.string({ required_error: 'Ödeme seçimi zorunludur.' }).uuid(),
  customerInvoiceId: z.string().uuid().optional(),
  subConsultantInvoiceId: z.string().uuid().optional(),
  allocatedAmount: decimalStr('Geçerli bir tutar giriniz.'),
  notes: z.string().max(500).optional(),
})

export const CreateSubConsultantPaymentSchema = z.object({
  invoiceId: z.string().uuid().optional(),
  hakedisId: z.string().uuid().optional(),
  vendorId: z.string({ required_error: 'Tedarikçi seçimi zorunludur.' }).uuid(),
  contractId: z.string({ required_error: 'Sözleşme seçimi zorunludur.' }).uuid(),
  projectId: z.string().uuid().optional(),
  amount: decimalStr('Geçerli bir tutar giriniz.'),
  currency: z.string().length(3).default('TRY'),
  paymentDate: z.string({ required_error: 'Ödeme tarihi zorunludur.' }),
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
  paymentMethod: PaymentMethod.default('BANK_TRANSFER'),
  instrumentId: z.string().uuid().optional(),
  documentNo: z.string().max(100).optional(),
  bankRef: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  notes: z.string().optional(),
})

/** Alt taşeron ödemesi düzenleme: yalnızca değiştirilebilen alanlar (tutar metin/Decimal) */
export const UpdateSubConsultantPaymentSchema = CreateSubConsultantPaymentSchema
  .pick({ amount: true, paymentDate: true, paymentMethod: true, period: true })
  .partial()
  .extend({
    documentNo: z.string().max(100).nullish(),
    notes: z.string().nullish(),
    hakedisId: z.string().uuid().nullish(),
    categoryId: z.string().uuid().nullish(),
    categoryCode: z.string().max(50).nullish(),
  })
export type UpdateSubConsultantPaymentInput = z.infer<typeof UpdateSubConsultantPaymentSchema>

/**
 * Alt yüklenici (tedarikçi) kaydı. Daha önce oluşturma ucunda doğrulama yoktu: geçersiz stopaj oranı sunucu hatasına (500) düşüyordu.
 * Boş metin "alanı temizle" anlamındadır (rota boş değeri null'a çevirir).
 */
export const SubConsultantVendorSchema = z.object({
  name: z.string({ required_error: 'Firma adı zorunludur.' }).trim().min(1, 'Firma adı zorunludur.').max(200),
  isCompany: z.boolean().optional(),
  isEFatura: z.boolean().optional(),
  vknTckn: z.string().max(11, 'VKN en çok 10, TCKN 11 hane olmalıdır.').optional(),
  contactName: z.string().max(200).optional(),
  contactEmail: z.union([z.literal(''), z.string().email('Geçerli bir e-posta adresi giriniz.').max(200)]).optional(),
  contactPhone: z.string().max(50).optional(),
  /** Stopaj oranı (%) — metin (Decimal) */
  stopajRate: z.union([z.literal(''), z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Geçerli bir oran giriniz.').refine((v) => Number(v) <= 100, 'Oran en çok 100 olabilir.')]).optional(),
  notes: z.string().max(500).optional(),
})
export const UpdateSubConsultantVendorSchema = SubConsultantVendorSchema.partial().extend({ isSubConsultant: z.boolean().optional() })
export type SubConsultantVendorInput = z.infer<typeof SubConsultantVendorSchema>
export type UpdateSubConsultantVendorInput = z.infer<typeof UpdateSubConsultantVendorSchema>

/** Alt yüklenici sözleşmesi — oluşturma/düzenleme (parasal değerler metin/Decimal) */
export const CreateSubContractSchema = z.object({
  projectId: z.string().uuid(),
  vendorId: z.string().uuid(),
  contractNo: z.string().max(100).optional(),
  scope: z.string().min(1).max(2000),
  nteAmount: z.string().optional(),
  currency: z.string().length(3).default('TRY'),
  contractAmount: z.string().min(1),
  kdvTevkifatNum: z.number().int().min(0).max(10).default(0),
  kdvTevkifatDen: z.number().int().min(1).max(10).default(10),
  kdvRate: z.string().default('20'),
  markupRate: z.string().default('0'),
  payMixCashPct: z.string().nullable().optional(),
  payMixInstrumentPct: z.string().nullable().optional(),
  payMixBarterPct: z.string().nullable().optional(),
  startDate: z.string(),
  endDate: z.string().optional(),
  notes: z.string().optional(),
})
export const UpdateSubContractSchema = CreateSubContractSchema.partial().omit({ projectId: true, vendorId: true })
export const SubContractStatusSchema = z.object({
  status: z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'TERMINATED']),
  reason: z.string().max(500).optional(),
})
/** Alt yüklenici sözleşme iş kalemi (BoQ) — toplu oluşturma dizisi ve tekil güncelleme */
export const CreateSubBoQItemsSchema = z.array(
  z.object({
    pozNo: z.string().max(50),
    description: z.string().min(1),
    unit: z.string().max(30).default('UNIT'),
    contractQuantity: z.string(),
    unitPrice: z.string(),
    sortOrder: z.number().int().optional(),
    block: z.string().max(50).nullable().optional(),
    floor: z.string().max(20).nullable().optional(),
  }),
)
export const UpdateSubBoQItemSchema = z.object({
  pozNo: z.string().max(50).optional(),
  description: z.string().min(1).optional(),
  unit: z.string().max(30).optional(),
  contractQuantity: z.string().optional(),
  unitPrice: z.string().optional(),
  sortOrder: z.number().int().optional(),
  block: z.string().max(50).nullable().optional(),
  floor: z.string().max(20).nullable().optional(),
  /** Proje (ana sözleşme) pozuna eşleştirme — null gönderilirse bağ kaldırılır */
  projectBoqItemId: z.string().uuid().nullable().optional(),
})
export type CreateSubBoQItemsInput = z.input<typeof CreateSubBoQItemsSchema>
export type UpdateSubBoQItemInput = z.input<typeof UpdateSubBoQItemSchema>
export type SubContractStatusInput = z.infer<typeof SubContractStatusSchema>
export type CreateSubContractInput = z.input<typeof CreateSubContractSchema>
export type UpdateSubContractInput = z.input<typeof UpdateSubContractSchema>

// ─── BoQ & Kesinti Şemaları ───────────────────────────────────────────────────

export const DeductionMethodEnum = z.enum(['PERCENTAGE', 'FIXED_AMOUNT'])
export const DeductionBaseEnum = z.enum(['GROSS', 'SUBTOTAL', 'NET_AFTER_KDV'])

/** Sözleşme iş kalemi (BoQ) — toplu import dizisi */
export const CreateBoQItemSchema = z.object({
  pozNo: z.string({ required_error: 'Poz no zorunludur.' }).min(1).max(50),
  description: z.string({ required_error: 'Açıklama zorunludur.' }).min(1).max(500),
  unit: z.string({ required_error: 'Birim zorunludur.' }).min(1).max(20),
  contractQuantity: decimalStr('Geçerli bir miktar giriniz.'),
  unitPrice: decimalStr('Geçerli bir birim fiyat giriniz.'),
  notes: z.string().max(500).nullable().optional(),
  block: z.string().max(50).nullable().optional(),
  floor: z.string().max(20).nullable().optional(),
})
export const CreateBoQItemsSchema = z.array(CreateBoQItemSchema).min(1, 'En az bir iş kalemi giriniz.')

export const UpdateBoQItemSchema = CreateBoQItemSchema.partial()

/** Kesinti tanımı — proje bazlı */
export const CreateDeductionConfigSchema = z.object({
  label: z.string({ required_error: 'Etiket zorunludur.' }).min(1).max(100),
  deductionType: z.string({ required_error: 'Tür zorunludur.' }).min(1).max(50),
  method: DeductionMethodEnum,
  rate: decimalStr().optional(),
  fixedAmount: decimalStr().optional(),
  appliedOn: DeductionBaseEnum.default('GROSS'),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0),
})

export const UpdateDeductionConfigSchema = CreateDeductionConfigSchema.partial()

/** Fiyat farkı ayarı — proje bazlı */
export const UpsertEscalationConfigSchema = z.object({
  baseIndexMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  baseIndexValue: decimalStr(),
  formulaNotes: z.string().max(1000).optional(),
  isActive: z.boolean().default(true),
})

/** Fiyat farkı endeks girişi */
export const CreateEscalationEntrySchema = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM formatında olmalıdır.'),
  indexValue: decimalStr('Geçerli bir endeks değeri giriniz.'),
  source: z.string().max(100).optional(),
})

/** İnşaat hakediş girişi — BoQ kümülatif/dönem hesaplama */
export const ConstructionBoQEntrySchema = z.object({
  boqItemId: z.string().uuid(),
  /** Birim fiyat modunda kümülatif miktar, götürü bedelde kümülatif yüzde */
  cumulativeQuantity: decimalStr().optional(),
  cumulativePercentage: decimalStr().optional(),
})

export const CreateConstructionInvoiceSchema = z.object({
  projectId: z.string({ required_error: 'Proje seçimi zorunludur.' }).uuid(),
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  title: z.string({ required_error: 'Başlık zorunludur.' }).min(1).max(200),
  kdvRate: z.coerce.number().min(0).max(100).default(20),
  notes: z.string().max(1000).optional(),
  boqEntries: z.array(ConstructionBoQEntrySchema).min(1, 'En az bir iş kalemi girişi zorunludur.'),
  /** Elle düzenlenmiş kesintiler (opsiyonel, yoksa otomatik hesaplanır) */
  deductionOverrides: z.array(z.object({
    configId: z.string().uuid(),
    overrideAmount: decimalStr().optional(),
  })).optional(),
})

export type CreateBoQItemInput = z.infer<typeof CreateBoQItemSchema>
export type CreateBoQItemsInput = z.infer<typeof CreateBoQItemsSchema>
export type UpdateBoQItemInput = z.infer<typeof UpdateBoQItemSchema>
export type CreateDeductionConfigInput = z.infer<typeof CreateDeductionConfigSchema>
export type UpdateDeductionConfigInput = z.infer<typeof UpdateDeductionConfigSchema>
export type UpsertEscalationConfigInput = z.infer<typeof UpsertEscalationConfigSchema>
export type CreateEscalationEntryInput = z.infer<typeof CreateEscalationEntrySchema>
export type ConstructionBoQEntryInput = z.infer<typeof ConstructionBoQEntrySchema>
export type CreateConstructionInvoiceInput = z.infer<typeof CreateConstructionInvoiceSchema>

// ─── Taşeron Hakediş Şemaları ────────────────────────────────────────────────

export const SubHakedisItemCategory = z.enum([
  'BOQ_WORK', 'ADDITIONAL_WORK', 'MATERIAL_ON_SITE', 'TRANSPORT',
  'MEALS', 'LABOR', 'DIRECT_MATERIAL', 'ADVANCE', 'OTHER',
])

export const SubHakedisStatus = z.enum([
  'DRAFT', 'SUBMITTED', 'APPROVED', 'INVOICED', 'REJECTED',
])

const flexDecimal = (msg = 'Geçerli bir tutar giriniz.') =>
  z.string().regex(/^-?\d+(\.\d{1,4})?$/, msg)

export const CreateSubHakedisItemSchema = z.object({
  category: SubHakedisItemCategory,
  customCategory: z.string().max(100).optional(),
  description: z.string().min(1).max(500),
  boqItemId: z.string().uuid().optional(),
  quantity: flexDecimal().optional(),
  unit: z.string().max(30).optional(),
  unitPrice: flexDecimal().optional(),
  amount: flexDecimal('Geçerli bir tutar giriniz.'),
  cumulativeQuantity: flexDecimal().optional(),
  cumulativePercentage: flexDecimal().optional(),
})

export const CreateSubHakedisSchema = z.object({
  contractId: z.string({ required_error: 'Sözleşme seçimi zorunludur.' }).uuid(),
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  kdvRate: z.coerce.number().min(0).max(100).default(20),
  kdvTevkifatNum: z.coerce.number().int().min(0).max(10).default(0),
  kdvTevkifatDen: z.coerce.number().int().min(1).max(10).default(10),
  priceAdjAmount: flexDecimal().optional().default('0'),
  notes: z.string().max(1000).optional(),
  productionItemIds: z.array(z.string().uuid()).min(1, 'En az bir imalat kaydı seçiniz.'),
  deductions: z.array(z.object({
    label: z.string().min(1).max(100),
    method: DeductionMethodEnum,
    rate: flexDecimal().optional(),
    baseAmount: flexDecimal().optional(),
    deductionAmount: flexDecimal('Geçerli bir kesinti tutarı giriniz.'),
  })).optional(),
})

export const UpdateSubHakedisSchema = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
  kdvRate: z.coerce.number().min(0).max(100).optional(),
  kdvTevkifatNum: z.coerce.number().int().min(0).max(10).optional(),
  kdvTevkifatDen: z.coerce.number().int().min(1).max(10).optional(),
  priceAdjAmount: flexDecimal().optional(),
  notes: z.string().max(1000).optional(),
  productionItemIds: z.array(z.string().uuid()).min(1, 'En az bir imalat kaydı seçiniz.').optional(),
  deductions: z.array(z.object({
    label: z.string().min(1).max(100),
    method: DeductionMethodEnum,
    rate: flexDecimal().optional(),
    baseAmount: flexDecimal().optional(),
    deductionAmount: flexDecimal('Geçerli bir kesinti tutarı giriniz.'),
  })).optional(),
})

export const SubHakedisStatusTransitionSchema = z.object({
  action: z.enum(['SUBMIT', 'APPROVE', 'REJECT', 'REOPEN', 'REVERT_APPROVAL']),
  rejectionReason: z.string().max(500).optional(),
})

// ─── Alt Yüklenici Fatura Aksiyonları (FE↔BE ortak sözleşme) ──────────────
// Bu şemalar hem backend route'ta hem frontend payload tipinde kullanılır;
// böylece action/alan-adı uyumsuzlukları derleme zamanında yakalanır.
export const SubInvoiceReviewActionEnum = z.enum(['APPROVE', 'REJECT', 'RESUBMIT'])

export const SubInvoiceReviewSchema = z.object({
  action: SubInvoiceReviewActionEnum,
  comment: z.string().max(1000).optional(),
  scheduledDate: z.string().optional(),
})

export const SubInvoicePaidSchema = z.object({
  paidDate: z.string({ required_error: 'Ödeme tarihi zorunludur.' }),
  /** Verilmezse BANK_TRANSFER */
  paymentMethod: PaymentMethod.optional(),
  /** Çek/senet yönteminde zorunlu (sunucu ödeme servisi denetler) */
  instrumentId: z.string().uuid().optional(),
})

export type SubInvoiceReviewAction = z.infer<typeof SubInvoiceReviewActionEnum>
export type SubInvoiceReviewInput = z.infer<typeof SubInvoiceReviewSchema>
export type SubInvoicePaidInput = z.infer<typeof SubInvoicePaidSchema>

export type SubHakedisItemCategoryType = z.infer<typeof SubHakedisItemCategory>
export type SubHakedisStatusType = z.infer<typeof SubHakedisStatus>
export type CreateSubHakedisInput = z.infer<typeof CreateSubHakedisSchema>
export type UpdateSubHakedisInput = z.infer<typeof UpdateSubHakedisSchema>
export type CreateSubHakedisItemInput = z.infer<typeof CreateSubHakedisItemSchema>

// ─── Müşteri Hakediş Şemaları ─────────────────────────────────────────────

export const CustomerHakedisItemCategory = z.enum([
  'BOQ_WORK', 'ADDITIONAL_WORK', 'MATERIAL_ON_SITE', 'TRANSPORT',
  'MEALS', 'LABOR', 'DIRECT_MATERIAL', 'ADVANCE', 'OTHER',
])

export const CustomerHakedisStatus = z.enum([
  'DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED',
])

export const CreateCustomerHakedisItemSchema = z.object({
  category: CustomerHakedisItemCategory,
  customCategory: z.string().max(100).optional(),
  description: z.string().min(1).max(500),
  boqItemId: z.string().uuid().optional(),
  quantity: flexDecimal().optional(),
  unit: z.string().max(30).optional(),
  unitPrice: flexDecimal().optional(),
  amount: flexDecimal('Geçerli bir tutar giriniz.'),
  cumulativeQuantity: flexDecimal().optional(),
  cumulativePercentage: flexDecimal().optional(),
})

export const CreateCustomerHakedisSchema = z.object({
  projectId: z.string({ required_error: 'Proje seçimi zorunludur.' }).uuid(),
  customerId: z.string({ required_error: 'Müşteri seçimi zorunludur.' }).uuid(),
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  title: z.string().max(300).optional(),
  kdvRate: z.coerce.number().min(0).max(100).default(20),
  kdvTevkifatNum: z.coerce.number().int().min(0).max(10).default(0),
  kdvTevkifatDen: z.coerce.number().int().min(1).max(10).default(10),
  priceAdjAmount: flexDecimal().optional().default('0'),
  notes: z.string().max(1000).optional(),
  productionItemIds: z.array(z.string().uuid()).min(1, 'En az bir imalat kaydı seçiniz.'),
  deductions: z.array(z.object({
    label: z.string().min(1).max(100),
    method: DeductionMethodEnum,
    rate: flexDecimal().optional(),
    baseAmount: flexDecimal().optional(),
    deductionAmount: flexDecimal('Geçerli bir kesinti tutarı giriniz.'),
  })).optional(),
})

export const CreateCustomerHakedisBoQSchema = z.object({
  projectId: z.string({ required_error: 'Proje seçimi zorunludur.' }).uuid(),
  customerId: z.string({ required_error: 'Müşteri seçimi zorunludur.' }).uuid(),
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  title: z.string().max(300).optional(),
  kdvRate: z.coerce.number().min(0).max(100).default(20),
  kdvTevkifatNum: z.coerce.number().int().min(0).max(10).default(0),
  kdvTevkifatDen: z.coerce.number().int().min(1).max(10).default(10),
  priceAdjAmount: flexDecimal().optional().default('0'),
  notes: z.string().max(1000).optional(),
  boqEntries: z.array(z.object({
    boqItemId: z.string().uuid(),
    periodQuantity: flexDecimal('Geçerli bir miktar giriniz.'),
  })).min(1, 'En az bir BoQ kalemi gereklidir.'),
  deductions: z.array(z.object({
    label: z.string().min(1).max(100),
    method: DeductionMethodEnum,
    rate: flexDecimal().optional(),
    baseAmount: flexDecimal().optional(),
    deductionAmount: flexDecimal('Geçerli bir kesinti tutarı giriniz.'),
  })).optional(),
})

export const UpdateCustomerHakedisSchema = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
  title: z.string().max(300).optional(),
  kdvRate: z.coerce.number().min(0).max(100).optional(),
  kdvTevkifatNum: z.coerce.number().int().min(0).max(10).optional(),
  kdvTevkifatDen: z.coerce.number().int().min(1).max(10).optional(),
  priceAdjAmount: flexDecimal().optional(),
  notes: z.string().max(1000).optional(),
  productionItemIds: z.array(z.string().uuid()).min(1, 'En az bir imalat kaydı seçiniz.').optional(),
  deductions: z.array(z.object({
    label: z.string().min(1).max(100),
    method: DeductionMethodEnum,
    rate: flexDecimal().optional(),
    baseAmount: flexDecimal().optional(),
    deductionAmount: flexDecimal('Geçerli bir kesinti tutarı giriniz.'),
  })).optional(),
})

export const CustomerHakedisStatusTransitionSchema = z.object({
  action: z.enum(['SUBMIT', 'APPROVE', 'REJECT', 'REOPEN', 'REVERT_APPROVAL']),
  rejectionReason: z.string().max(500).optional(),
})

export type CustomerHakedisItemCategoryType = z.infer<typeof CustomerHakedisItemCategory>
export type CustomerHakedisStatusType = z.infer<typeof CustomerHakedisStatus>
export type CreateCustomerHakedisInput = z.infer<typeof CreateCustomerHakedisSchema>
export type CreateCustomerHakedisBoQInput = z.infer<typeof CreateCustomerHakedisBoQSchema>
export type UpdateCustomerHakedisInput = z.infer<typeof UpdateCustomerHakedisSchema>
export type CreateCustomerHakedisItemInput = z.infer<typeof CreateCustomerHakedisItemSchema>

export type EmployeeCostInput = z.infer<typeof EmployeeCostSchema>
export type OverheadExpenseInput = z.infer<typeof OverheadExpenseSchema>
export type TenantFinanceSettingsInput = z.infer<typeof TenantFinanceSettingsSchema>
export type CreatePaymentInstrumentInput = z.infer<typeof CreatePaymentInstrumentSchema>
export type UpdatePaymentInstrumentInput = z.infer<typeof UpdatePaymentInstrumentSchema>
export type InstrumentStatusTransitionInput = z.infer<typeof InstrumentStatusTransitionSchema>
export type CreatePaymentAllocationInput = z.infer<typeof CreatePaymentAllocationSchema>
export type CreateSubConsultantPaymentInput = z.infer<typeof CreateSubConsultantPaymentSchema>

// ─── Müşteri ödemeleri (Gelirler › Ödemeler) ────────────────────────────────────

const optionalUuid = z.string().uuid().nullish()
const optionalText = (max: number) => z.string().max(max).nullish()

export const CustomerPaymentType = z.enum(['PROJECT_INVOICE', 'OTHER_INCOME'])

export const CreateCustomerPaymentSchema = z.object({
  paymentType: CustomerPaymentType.default('PROJECT_INVOICE'),
  customerId: optionalUuid,
  projectId: optionalUuid,
  invoiceId: optionalUuid,
  hakedisId: optionalUuid,
  instrumentId: optionalUuid,
  categoryId: optionalUuid,
  categoryCode: optionalText(50),
  /** Parasal değer metin (Prisma Decimal) — 12 tam + 2 ondalık hane */
  amount: z.string({ required_error: 'Tutar zorunludur.' }).regex(/^\d{1,13}(\.\d{1,2})?$/, 'Geçerli bir tutar giriniz (örn. 1250.50).')
    .refine((v) => Number(v) > 0, 'Tutar sıfırdan büyük olmalıdır.'),
  currency: z.enum(['TRY', 'USD', 'EUR', 'GBP']).default('TRY'),
  paymentDate: z.string({ required_error: 'Ödeme tarihi zorunludur.' }).date('Geçerli bir tarih giriniz (YYYY-AA-GG).'),
  /** Boşsa ödeme tarihinden türetilir (YYYY-AA) */
  period: z.string().regex(/^\d{4}-\d{2}$/, 'Dönem YYYY-AA biçiminde olmalıdır.').nullish(),
  paymentMethod: PaymentMethod.default('BANK_TRANSFER'),
  receivedFrom: optionalText(200),
  documentNo: optionalText(100),
  bankRef: optionalText(100),
  description: optionalText(1000),
  notes: optionalText(2000),
})

export const UpdateCustomerPaymentSchema = CreateCustomerPaymentSchema.partial()

export type CreateCustomerPaymentInput = z.infer<typeof CreateCustomerPaymentSchema>
export type UpdateCustomerPaymentInput = z.infer<typeof UpdateCustomerPaymentSchema>

// ─── İmalat Kaydı Şemaları ───────────────────────────────────────────────────

/** Form sayısı: metin ya da sayı gelebilir; sayıya çevrilemeyen ya da sonsuz değer reddedilir (NaN veritabanına gitmesin) */
const numeric = z.union([z.string(), z.number()]).transform(v => (typeof v === 'string' && v.trim() === '' ? NaN : Number(v))).refine(Number.isFinite, 'Geçerli bir sayı giriniz.')

export const CreateProductionItemSchema = z.object({
  projectId: z.string().uuid(),
  pozNo: z.string().max(30).default(''),
  description: z.string().min(1).max(500),
  unit: z.string().min(1).max(20),
  quantity: numeric,
  unitPrice: numeric,
  completionDate: z.string().optional(),
  vendorId: z.string().uuid().optional(),
  boqItemId: z.string().uuid().optional(),
  subBoqItemId: z.string().uuid().optional(),
  progressItemId: z.string().uuid().optional(),
})

export const UpdateProductionItemSchema = z.object({
  pozNo: z.string().max(30).optional(),
  description: z.string().min(1).max(500).optional(),
  unit: z.string().min(1).max(20).optional(),
  quantity: numeric.optional(),
  unitPrice: numeric.optional(),
  completionDate: z.string().nullable().optional(),
  vendorId: z.string().uuid().nullable().optional(),
  boqItemId: z.string().uuid().nullable().optional(),
  subBoqItemId: z.string().uuid().nullable().optional(),
  progressItemId: z.string().uuid().nullable().optional(),
})
export type CreateProductionItemInput = z.input<typeof CreateProductionItemSchema>
export type UpdateProductionItemInput = z.input<typeof UpdateProductionItemSchema>

// ─── Alt Yüklenici Fatura (oluştur/düzenle) ──────────────────────────────────

export const BoQEntryInputSchema = z.object({
  boqItemId: z.string().uuid(),
  cumulativeQuantity: z.number().min(0).optional(),
  cumulativePercentage: z.number().min(0).max(100).optional(),
})

export const CreateSubInvoiceSchema = z.object({
  contractId: z.string().uuid(),
  invoiceNo: z.string().min(1).max(100),
  eInvoiceUuid: z.string().uuid().optional(),
  invoiceDate: z.string(),
  netAmount: z.string(),
  // true ise netAmount KDV dahil girilmiştir; sunucu matraha çevirir (BoQ modunda yok sayılır)
  amountIncludesKdv: z.boolean().default(false),
  // Onaylı hakedişe bağla — bağlanınca hakediş INVOICED olur
  hakedisId: z.string().uuid().optional(),
  progressId: z.string().uuid().optional(),
  payWhenPaidRef: z.string().max(200).optional(),
  notes: z.string().optional(),
  // İsteğe bağlı BoQ kalemleri — pricingMode UNIT_PRICE/LUMP_SUM ise kullanılır
  boqEntries: z.array(BoQEntryInputSchema).optional(),
})

export const UpdateSubInvoiceSchema = z.object({
  invoiceNo: z.string().min(1).max(100).optional(),
  eInvoiceUuid: z.string().uuid().nullable().optional(),
  invoiceDate: z.string().optional(),
  netAmount: z.string().optional(),
  amountIncludesKdv: z.boolean().default(false),
  hakedisId: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
})
export type CreateSubInvoiceInput = z.input<typeof CreateSubInvoiceSchema>
export type UpdateSubInvoiceInput = z.input<typeof UpdateSubInvoiceSchema>

// ─── Hizmet Tedarikçisi (genel tedarikçi) ────────────────────────────────────

/** Boş metin "alanı temizle" anlamındadır (rota boş değeri null'a çevirir) */
export const ServiceVendorSchema = z.object({
  name: z.string({ required_error: 'İsim zorunludur.' }).trim().min(1, 'İsim zorunludur.').max(200),
  category: z.string().max(50).optional(),
  contactName: z.string().max(100).nullish(),
  contactEmail: z.union([z.literal(''), z.string().email('Geçerli bir e-posta adresi giriniz.').max(200)]).nullish(),
  contactPhone: z.string().max(30).nullish(),
  notes: z.string().max(2000).nullish(),
})
export const UpdateServiceVendorSchema = ServiceVendorSchema.partial().extend({ isActive: z.boolean().optional() })
export type ServiceVendorInput = z.input<typeof ServiceVendorSchema>
export type UpdateServiceVendorInput = z.input<typeof UpdateServiceVendorSchema>

// ─── Maaş / Bordro Ödemesi (tekil kayıt) ─────────────────────────────────────

export const SalaryPaymentSchema = z.object({
  userId: z.string({ required_error: 'Çalışan seçimi zorunludur.' }).uuid(),
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-AA formatında olmalıdır.'),
  /** SALARY | SGK_EMPLOYER | SGK_EMPLOYEE | INCOME_TAX | STAMP_TAX | BONUS | ... (serbest metin; şema kataloğu arayüzde) */
  type: z.string().min(1).max(30).default('SALARY'),
  amount: decimalStr('Geçerli bir tutar giriniz.'),
  /** Yalnızca SALARY için: amount personele ödenen NET mi, BRÜT mü? Boşsa GROSS (eski istemci davranışı). Arayüz varsayılanı NET. */
  amountBasis: z.enum(['NET', 'GROSS']).optional(),
  currency: z.string().length(3).default('TRY'),
  paymentDate: z.string({ required_error: 'Ödeme tarihi zorunludur.' }).min(1),
  bankRef: z.string().max(100).nullish(),
  notes: z.string().max(500).nullish(),
})
export const UpdateSalaryPaymentSchema = SalaryPaymentSchema.partial().omit({ userId: true })
export type SalaryPaymentInput = z.input<typeof SalaryPaymentSchema>
export type UpdateSalaryPaymentInput = z.input<typeof UpdateSalaryPaymentSchema>

// ─── Maaş Profili (çalışan başına brüt maaş ve sabit yan ödemeler) ────────────

export const SalaryProfileSchema = z.object({
  grossSalary: decimalStr('Geçerli bir brüt maaş giriniz.'),
  /** SGK işveren payı yüzdesi (varsayılan 21.75) */
  sgkEmployerRate: decimalStr('Geçerli bir oran giriniz.').optional(),
  mealAllowance: decimalStr().optional(),
  transportAllowance: decimalStr().optional(),
  otherFixed: decimalStr().optional(),
  /** Sigortasız çalışan: SGK/vergi hesaplanmaz */
  isUninsured: z.boolean().optional(),
  /** Emekli çalışan (sigortasızsa yok sayılır) */
  isRetired: z.boolean().optional(),
  notes: z.string().max(500).nullish(),
})
export type SalaryProfileInput = z.input<typeof SalaryProfileSchema>

// ─── Alt yüklenici: değişiklik emri, sertifika, BoQ metinden içe aktarma ──────

export const CreateSubChangeOrderSchema = z.object({
  coNumber: z.string().min(1).max(50),
  description: z.string().min(1),
  /** Tutar değişimi (eksi olabilir) — metin/Decimal */
  amountChange: z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'Geçerli bir tutar giriniz.'),
})
export const SUB_CERT_TYPES = ['SGK_CLEARANCE', 'TAX_CLEARANCE', 'OTHER'] as const
export const CreateSubCertificateSchema = z.object({
  vendorId: z.string().uuid(),
  certType: z.enum(SUB_CERT_TYPES),
  documentNo: z.string().max(100).optional(),
  issuedDate: z.string(),
  expiresDate: z.string().optional(),
  fileUrl: z.string().url().optional(),
  notes: z.string().max(500).optional(),
})
/** Güncellemede boş metin alanı temizler (rota boşu null'a çevirir) */
export const UpdateSubCertificateSchema = z.object({
  certType: z.enum(SUB_CERT_TYPES).optional(),
  documentNo: z.string().max(100).nullish(),
  issuedDate: z.string().optional(),
  expiresDate: z.string().nullish(),
  notes: z.string().max(500).nullish(),
})
/** Sekmeyle ayrılmış satırlar: pozNo, açıklama, birim, miktar, birim fiyat */
export const SubBoqTextImportSchema = z.object({ text: z.string().min(1, 'İçe aktarılacak metin zorunludur.') })
export type CreateSubChangeOrderInput = z.infer<typeof CreateSubChangeOrderSchema>
export type CreateSubCertificateInput = z.infer<typeof CreateSubCertificateSchema>
export type UpdateSubCertificateInput = z.infer<typeof UpdateSubCertificateSchema>
export type SubBoqTextImportInput = z.infer<typeof SubBoqTextImportSchema>

// ─── Toplu Bordro ve Ödeme Talimatı ───────────────────────────────────────────

/** `POST /finance/salary-payments/payroll`: dönem için çalışan başına ödeme satırları (tutarlar metin) */
export const PayrollRunSchema = z.object({
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-AA formatında olmalıdır.'),
  paymentDate: z.string({ required_error: 'Ödeme tarihi zorunludur.' }).min(1, 'Ödeme tarihi zorunludur.'),
  bankRefPrefix: z.string().max(60).optional(),
  /** Aynı dönemde kayıt varsa bile yaz (çift kayıt onayı) */
  force: z.boolean().optional(),
  employees: z.array(z.object({
    userId: z.string().uuid(),
    lines: z.array(z.object({ type: z.string().min(1).max(30), amount: z.coerce.string(), amountBasis: z.enum(['NET', 'GROSS']).optional() })),
  })).min(1, 'En az bir çalışan zorunludur.'),
})

/** `POST /finance/salary-payments/payment-instruction-pdf`: banka ödeme talimatı */
export const PaymentInstructionSchema = z.object({
  period: z.string({ required_error: 'Dönem zorunludur.' }).regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Dönem YYYY-AA formatında olmalıdır.'),
  paymentDate: z.string({ required_error: 'Ödeme tarihi zorunludur.' }).min(1, 'Ödeme tarihi zorunludur.'),
  note: z.string().max(500).optional(),
  employees: z.array(z.object({
    name: z.string().min(1).max(200),
    nationalId: z.string().max(20).nullish(),
    iban: z.string().max(40).nullish(),
    bankName: z.string().max(100).nullish(),
    netAmount: z.number().positive(),
  })).min(1, 'En az bir personel zorunludur.'),
})
export type PayrollRunInput = z.input<typeof PayrollRunSchema>
export type PaymentInstructionInput = z.input<typeof PaymentInstructionSchema>
